import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { Languages, ShoppingCart, Menu, ChevronDown, User, Building2 } from "lucide-react";
import { useCart } from "@/hooks/useCart";
import { useDeferredVisibleSitePages } from "@/hooks/useDeferredSitePages";
import { useSiteLogo } from "@/hooks/useSiteLogo";
import { useCustomerType, CUSTOMER_TYPE_LABEL } from "@/contexts/CustomerTypeContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

// Static use-case items for the new "Användningsområden" dropdown.
// These pages live as static routes (not in site_pages DB), so the menu
// always renders them regardless of the page-visibility system.
const USE_CASE_ITEMS: { slug: string; sv: string; en: string; de: string }[] = [
  { slug: "tradgardskupol", sv: "Trädgårdskupol", en: "Garden dome", de: "Gartenkuppel" },
  { slug: "kupol-vaxthus", sv: "Växthus & orangeri", en: "Greenhouse & orangery", de: "Gewächshaus & Orangerie" },
  { slug: "glamping-kupol", sv: "Glamping", en: "Glamping", de: "Glamping" },
];

// FAQ label per language (kept inline to avoid translation-context changes)
const FAQ_LABEL: Record<"sv" | "en" | "de", string> = {
  sv: "FAQ",
  en: "FAQ",
  de: "FAQ",
};

// Translations for page names
const pageTranslations: Record<string, {
  sv: string;
  en: string;
  de: string;
}> = {
  'catalog_polycarbonate': {
    sv: 'Panoramakupoler',
    en: 'Panorama Domes',
    de: 'Panorama-Kuppeln'
  },
  'catalog_bioceramic': {
    sv: 'Biokeramiska kupoler',
    en: 'Bioceramic Domes',
    de: 'Biokeramische Kuppeln'
  },
  'catalog_nova': {
    sv: 'Nova Domes',
    en: 'Nova Domes',
    de: 'Nova Kuppeln'
  },
  'catalog_lampor': {
    sv: 'Lampor',
    en: 'Lamps',
    de: 'Lampen'
  },
  'experiences_visit': {
    sv: 'Besök en Dome',
    en: 'Visit a Dome',
    de: 'Besuchen Sie eine Kuppel'
  },
  'experiences_stay': {
    sv: 'Övernatta i Dome',
    en: 'Stay in a Dome',
    de: 'Übernachten in einer Kuppel'
  },
  'experiences_host': {
    sv: 'Bli Dome-värd',
    en: 'Become a Host',
    de: 'Werden Sie Gastgeber'
  },
  'experiences_dome_village': {
    sv: 'Bo i en dome-by',
    en: 'Live in a Dome Village',
    de: 'Leben in einem Dome-Dorf'
  }
};

// Static fallback data for immediate rendering (no Supabase wait)
const STATIC_FALLBACK = {
  mainPages: ['home', 'shop', 'blog', 'about', 'contact'] as const,
  catalogItems: [
    { id: 'fb-poly', page_key: 'catalog_polycarbonate', path: '/panoramakupoler', name: 'Panoramakupoler' },
    { id: 'fb-bio', page_key: 'catalog_bioceramic', path: '/biokeramiska-domes', name: 'Biokeramiska kupoler' },
    { id: 'fb-nova', page_key: 'catalog_nova', path: '/nova-domes', name: 'Nova Domes' },
    { id: 'fb-lamp', page_key: 'catalog_lampor', path: '/lampor', name: 'Lampor' },
  ] as const,
  experienceItems: [
    { id: 'fb-host', page_key: 'experiences_host', path: '/upplevelser/bli-vard', name: 'Bli Dome-värd' },
    { id: 'fb-village', page_key: 'experiences_dome_village', path: '/upplevelser/dome-village', name: 'Bo i en dome-by' },
  ] as const,
};

export const Navigation = () => {
  const {
    language,
    setLanguage,
    t
  } = useLanguage();
  const { customerType, setCustomerType } = useCustomerType();
  const {
    totalItems
  } = useCart();
  const {
    data: pages,
    isLoading
  } = useDeferredVisibleSitePages();

  const navLogo = useSiteLogo("/lovable-uploads/9fecb164-4a60-456b-a8e8-c39cca3bb492.webp");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catalogExpanded, setCatalogExpanded] = useState(false);
  const [experiencesExpanded, setExperiencesExpanded] = useState(false);
  const [useCasesExpanded, setUseCasesExpanded] = useState(false);

  // Use static fallback while loading, then switch to real data
  const useStaticFallback = isLoading || !pages;

  const mainPages = useStaticFallback 
    ? STATIC_FALLBACK.mainPages.map(key => ({ page_key: key }))
    : pages.filter(p => !p.parent_key);
  
  const catalogItems = useStaticFallback
    ? STATIC_FALLBACK.catalogItems
    : pages.filter(p => p.parent_key === 'catalog');
  
  const experienceItems = useStaticFallback
    ? STATIC_FALLBACK.experienceItems
    : pages.filter(p => p.parent_key === 'experiences');

  const isPageVisible = (key: string) => mainPages.some(p => p.page_key === key);

  const isCatalogVisible = catalogItems.length > 0;
  const isExperiencesVisible = experienceItems.length > 0;

  const isShopVisible = isPageVisible('shop');
  const isBlogVisible = isPageVisible('blog');
  const isAboutVisible = isPageVisible('about');
  const isContactVisible = isPageVisible('contact');
  const isHomeVisible = isPageVisible('home');

  const getTranslatedName = (pageKey: string, fallbackName: string) => {
    const translation = pageTranslations[pageKey];
    return translation ? translation[language] : fallbackName;
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-b border-border">
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-3">
            <img 
              alt="Diamond Domes" 
              className="h-12 w-auto border-0 shadow-none rounded-none" 
              src={navLogo}
              width="96"
              height="48"
            />
          </Link>
          
          {/* Desktop Navigation */}
          <NavigationMenu className="hidden md:flex">
            <NavigationMenuList className="space-x-2">
              {isHomeVisible && (
                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link to="/" className="text-sm hover:text-primary transition-smooth px-4 py-2 inline-block">
                      {t("nav_home")}
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>
              )}
              
              {isCatalogVisible && (
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="text-sm">
                    {t("nav_catalog")}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[200px] gap-1 p-2 bg-popover">
                      {catalogItems.map(item => (
                        <li key={item.id}>
                          <NavigationMenuLink asChild>
                            <Link to={item.path} className="block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground text-sm">
                              {getTranslatedName(item.page_key, item.name)}
                            </Link>
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              )}

              <NavigationMenuItem>
                <NavigationMenuTrigger className="text-sm">
                  {t("nav_usecases")}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[240px] gap-1 p-2 bg-popover">
                    <li>
                      <NavigationMenuLink asChild>
                        <Link to="/anvandningsomraden" className="block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground text-sm font-medium">
                          {t("nav_usecases")} →
                        </Link>
                      </NavigationMenuLink>
                    </li>
                    {USE_CASE_ITEMS.map(item => (
                      <li key={item.slug}>
                        <NavigationMenuLink asChild>
                          <Link to={`/anvandningsomraden/${item.slug}`} className="block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground text-sm">
                            {item[language]}
                          </Link>
                        </NavigationMenuLink>
                      </li>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>

              {isShopVisible && (
                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link to="/shop" className="relative text-sm hover:text-primary transition-smooth px-4 py-2 inline-block">
                      <span className="absolute -inset-1 rounded-md ring-2 ring-green-400 ring-offset-2 ring-offset-background animate-pulse pointer-events-none" />
                      <span className="relative z-10">{t("nav_webshop")}</span>
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>
              )}

              {isBlogVisible && (
                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link to="/blogg" className="text-sm hover:text-primary transition-smooth px-4 py-2 inline-block">
                      {t("nav_blog")}
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>
              )}

              <NavigationMenuItem>
                <NavigationMenuLink asChild>
                  <Link to="/faq" className="text-sm hover:text-primary transition-smooth px-4 py-2 inline-block">
                    {FAQ_LABEL[language]}
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>

              {isAboutVisible && (
                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link to="/about" className="text-sm hover:text-primary transition-smooth px-4 py-2 inline-block">
                      {t("nav_about")}
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>
              )}

              {isContactVisible && (
                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link to="/contact" className="text-sm hover:text-primary transition-smooth px-4 py-2 inline-block">
                      {t("nav_contact")}
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>
              )}

              {isExperiencesVisible && (
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="text-sm">
                    {t("nav_experiences")}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[200px] gap-1 p-2 bg-popover">
                      {experienceItems.map(item => (
                        <li key={item.id}>
                          <NavigationMenuLink asChild>
                            <Link to={item.path} className="block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground text-sm">
                              {getTranslatedName(item.page_key, item.name)}
                            </Link>
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              )}
            </NavigationMenuList>
          </NavigationMenu>
          
          <div className="flex items-center gap-2">
            {/* Cart Icon */}
            <Button variant="ghost" size="icon" asChild className="relative">
              <Link to="/shop/kundvagn" aria-label="Kundvagn">
                <ShoppingCart className="h-5 w-5" />
                {totalItems > 0 && (
                  <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-medium">
                    {totalItems}
                  </span>
                )}
              </Link>
            </Button>

            {/* Language Switcher */}
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Välj språk">
                <Languages className="h-5 w-5" />
              </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-card z-50">
                <DropdownMenuItem onClick={() => setLanguage("sv")} className={language === "sv" ? "bg-accent" : ""}>
                  <span className="mr-2 text-lg">🇸🇪</span> Svenska
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLanguage("en")} className={language === "en" ? "bg-accent" : ""}>
                  <span className="mr-2 text-lg">🇬🇧</span> English
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLanguage("de")} className={language === "de" ? "bg-accent" : ""}>
                  <span className="mr-2 text-lg">🇩🇪</span> Deutsch
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setCustomerType("private")} className={customerType === "private" ? "bg-accent" : ""}>
                  <User className="mr-2 h-4 w-4" /> {CUSTOMER_TYPE_LABEL.private[language]}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setCustomerType("business")} className={customerType === "business" ? "bg-accent" : ""}>
                  <Building2 className="mr-2 h-4 w-4" /> {CUSTOMER_TYPE_LABEL.business[language]}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile Hamburger Menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden" aria-label="Meny">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 p-0">
                <div className="flex flex-col py-8 px-6 space-y-1">
                  {isHomeVisible && (
                    <Link to="/" onClick={() => setMobileOpen(false)} className="py-3 text-foreground font-medium hover:text-primary transition-colors">
                      {t("nav_home")}
                    </Link>
                  )}

                  {isCatalogVisible && (
                    <Collapsible open={catalogExpanded} onOpenChange={setCatalogExpanded}>
                      <CollapsibleTrigger className="flex items-center justify-between w-full py-3 text-foreground font-medium hover:text-primary transition-colors">
                        {t("nav_catalog")}
                        <ChevronDown className={`h-4 w-4 transition-transform ${catalogExpanded ? 'rotate-180' : ''}`} />
                      </CollapsibleTrigger>
                      <CollapsibleContent className="pl-4 space-y-1">
                        {catalogItems.map(item => (
                          <Link
                            key={item.id}
                            to={item.path}
                            onClick={() => setMobileOpen(false)}
                            className="block py-2 text-sm text-muted-foreground hover:text-primary transition-colors"
                          >
                            {getTranslatedName(item.page_key, item.name)}
                          </Link>
                        ))}
                      </CollapsibleContent>
                    </Collapsible>
                  )}

                  <Collapsible open={useCasesExpanded} onOpenChange={setUseCasesExpanded}>
                    <CollapsibleTrigger className="flex items-center justify-between w-full py-3 text-foreground font-medium hover:text-primary transition-colors">
                      {t("nav_usecases")}
                      <ChevronDown className={`h-4 w-4 transition-transform ${useCasesExpanded ? 'rotate-180' : ''}`} />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="pl-4 space-y-1">
                      <Link
                        to="/anvandningsomraden"
                        onClick={() => setMobileOpen(false)}
                        className="block py-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
                      >
                        {t("nav_usecases")} →
                      </Link>
                      {USE_CASE_ITEMS.map(item => (
                        <Link
                          key={item.slug}
                          to={`/anvandningsomraden/${item.slug}`}
                          onClick={() => setMobileOpen(false)}
                          className="block py-2 text-sm text-muted-foreground hover:text-primary transition-colors"
                        >
                          {item[language]}
                        </Link>
                      ))}
                    </CollapsibleContent>
                  </Collapsible>

                  {isExperiencesVisible && (
                    <Collapsible open={experiencesExpanded} onOpenChange={setExperiencesExpanded}>
                      <CollapsibleTrigger className="flex items-center justify-between w-full py-3 text-foreground font-medium hover:text-primary transition-colors">
                        {t("nav_experiences")}
                        <ChevronDown className={`h-4 w-4 transition-transform ${experiencesExpanded ? 'rotate-180' : ''}`} />
                      </CollapsibleTrigger>
                      <CollapsibleContent className="pl-4 space-y-1">
                        {experienceItems.map(item => (
                          <Link
                            key={item.id}
                            to={item.path}
                            onClick={() => setMobileOpen(false)}
                            className="block py-2 text-sm text-muted-foreground hover:text-primary transition-colors"
                          >
                            {getTranslatedName(item.page_key, item.name)}
                          </Link>
                        ))}
                      </CollapsibleContent>
                    </Collapsible>
                  )}

                  {isShopVisible && (
                    <Link to="/shop" onClick={() => setMobileOpen(false)} className="py-3 text-foreground font-medium hover:text-primary transition-colors">
                      {t("nav_webshop")}
                    </Link>
                  )}

                  {isBlogVisible && (
                    <Link to="/blogg" onClick={() => setMobileOpen(false)} className="py-3 text-foreground font-medium hover:text-primary transition-colors">
                      {t("nav_blog")}
                    </Link>
                  )}

                  <Link to="/faq" onClick={() => setMobileOpen(false)} className="py-3 text-foreground font-medium hover:text-primary transition-colors">
                    {FAQ_LABEL[language]}
                  </Link>

                  {isAboutVisible && (
                    <Link to="/about" onClick={() => setMobileOpen(false)} className="py-3 text-foreground font-medium hover:text-primary transition-colors">
                      {t("nav_about")}
                    </Link>
                  )}

                  {isContactVisible && (
                    <Link to="/contact" onClick={() => setMobileOpen(false)} className="py-3 text-foreground font-medium hover:text-primary transition-colors">
                      {t("nav_contact")}
                    </Link>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};
